import { AlertTriangle, Lock, Unlock, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtTime } from "@/lib/format";
import { Confirm, StatusBadge, Timer, isOverdue } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { QueueHistoryDialog } from "./QueueHistoryDialog";

const BTN = "h-16 rounded-2xl text-lg font-extrabold tracking-wide";

function OnWallCard({ t, offset, busy, act }) {
  const over = isOverdue(t, offset);
  const b = (a) => busy === `${t.id}:${a}`;
  return (
    <div className={cn("rounded-3xl bg-white p-4 ring-2", over ? "overdue-pulse ring-red-500" : "ring-[#1A3323]/15")} data-testid={`onwall-${t.number}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-mono text-4xl font-extrabold text-[#1A3323]">{t.number}</div>
          <div className="text-base font-bold">{t.display_name}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={t.status} testid={`onwall-status-${t.number}`} />
          {over && <span className="flex items-center gap-1 rounded-full bg-red-600 px-2.5 py-0.5 text-xs font-extrabold text-white" data-testid={`overdue-badge-${t.number}`}><AlertTriangle className="h-3 w-3" /> OVERDUE</span>}
        </div>
      </div>
      {t.status === "CLIMBING" && <div className="mt-3 rounded-2xl bg-[#F7F4EA] p-4 text-center"><Timer startedAt={t.started_at} rotationMinutes={t.rotation_minutes} offset={offset} huge /><div className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-[#3D6F4B]">sedang manjat · standar {t.rotation_minutes}:00</div></div>}
      {t.status === "CALLED" && <div className="mt-1 text-xs text-muted-foreground">Dipanggil {fmtTime(t.called_at)}</div>}
      <div className="mt-3 grid grid-cols-3 gap-2">
        {t.status === "CALLED" && (
          <>
            <Button className={cn(BTN, "col-span-2 bg-[#3D6F4B] hover:bg-[#335e3f]")} disabled={!!busy} onClick={() => act(t.id, "arrive")} data-testid={`arrive-button-${t.number}`}>KONFIRMASI HADIR</Button>
            <Confirm testid={`skip-called-${t.number}`} title={`Skip ${t.number}?`} desc="Tiket menjadi SKIPPED (terminal). Peserta dapat mengambil tiket baru." destructive confirmText="Skip" onConfirm={() => act(t.id, "skip")}
              trigger={<Button variant="outline" className={cn(BTN, "border-2 border-[#2A2E2B] text-[#2A2E2B]")} disabled={!!busy} data-testid={`skip-button-${t.number}`}>SKIP</Button>} />
          </>
        )}
        {t.status === "ARRIVED" && <Button className={cn(BTN, "col-span-3 bg-[#1A3323] hover:bg-[#22422E]")} disabled={!!busy} onClick={() => act(t.id, "start")} data-testid={`start-button-${t.number}`}>{b("start") ? "..." : "MULAI"}</Button>}
        {t.status === "CLIMBING" && <Button className={cn(BTN, "col-span-3", over ? "bg-red-600 hover:bg-red-700" : "bg-[#1A3323] hover:bg-[#22422E]")} disabled={!!busy} onClick={() => act(t.id, "complete")} data-testid={`complete-button-${t.number}`}>{b("complete") ? "..." : "SELESAI"}</Button>}
      </div>
    </div>
  );
}

function NextUp({ t, busy, act }) {
  if (!t) return <div className="rounded-3xl border-2 border-dashed border-[#1A3323]/20 p-6 text-center text-sm text-muted-foreground" data-testid="next-empty">Tidak ada antrean menunggu.</div>;
  return (
    <div className="rounded-3xl bg-[#1A3323] p-4 text-[#FDFCF8]" data-testid="next-participant-card">
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#FDFCF8]/60">Peserta berikutnya</div>
      <div className="flex items-end justify-between">
        <div className="font-mono text-5xl font-extrabold" data-testid="next-ticket-number">{t.number}</div>
        <div className="pb-1 text-right text-lg font-bold">{t.display_name}</div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <Button className={cn(BTN, "col-span-2 bg-[#FDFCF8] text-[#1A3323] hover:bg-[#E3EDD9]")} disabled={!!busy} onClick={() => act(t.id, "call")} data-testid="call-next-button">PANGGIL</Button>
        <Confirm testid="skip-waiting" title={`Skip ${t.number}?`} desc="Tiket menjadi SKIPPED dan tidak bisa diaktifkan kembali." destructive confirmText="Skip" onConfirm={() => act(t.id, "skip")}
          trigger={<Button variant="outline" className={cn(BTN, "border-2 border-white/40 bg-transparent text-white hover:bg-white/10")} disabled={!!busy} data-testid="skip-next-button">SKIP</Button>} />
      </div>
    </div>
  );
}

export function RoutePanel({ r, offset, busy, act, canManage, onClose, onOpen }) {
  const closed = r.status !== "OPEN";
  return (
    <div className="space-y-4" data-testid={`route-panel-${r.code}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="break-words text-2xl font-extrabold text-[#1A3323]" data-testid={`route-title-${r.code}`}>
            {r.name}
            {r.grade && <span className="ml-2 rounded-md bg-[#E3EDD9] px-2 py-0.5 align-middle font-mono text-sm font-extrabold" data-testid={`route-grade-${r.code}`}>{r.grade}</span>}
          </h2>
          <span className="mt-1 inline-block text-sm font-semibold text-muted-foreground">
            {r.route_type === "LEAD" ? "LEAD" : "TOP ROPE"}
          </span>
          <div className="text-xs text-muted-foreground">Standar {r.rotation_minutes} mnt · rata-rata {Math.round(r.avg_seconds / 60 * 10) / 10} mnt ({r.avg_basis === "historical" ? "aktual" : "standar"}) · {r.completed_count} selesai</div>
        </div>
        {canManage && (closed ? (
          <Button variant="outline" className="rounded-full" onClick={onOpen} data-testid={`open-route-${r.code}`}><Unlock className="mr-1 h-4 w-4" /> Buka</Button>
        ) : (
          <Confirm testid={`close-route-${r.code}`} title={`Tutup ${r.name}?`} desc="Penutupan hanya menghentikan pengambilan tiket baru. Antrian yang sudah masuk tetap dapat dipanggil sampai selesai." destructive confirmText="Tutup Antrian" onConfirm={onClose}
            trigger={<Button variant="outline" className="rounded-full border-red-300 text-red-700" data-testid={`close-route-${r.code}-button`}><Lock className="mr-1 h-4 w-4" /> Tutup Antrian</Button>} />
        ))}
      </div>
      {closed && <div className="rounded-xl bg-stone-200 p-3 text-sm font-semibold text-stone-700" data-testid="route-closed-banner">Jalur ditutup untuk tiket baru. Antrian yang sudah masuk tetap berjalan.</div>}
      {r.on_wall.map((t) => <OnWallCard key={t.id} t={t} offset={offset} busy={busy} act={act} />)}
      <NextUp t={r.waiting[0]} busy={busy} act={act} />
      {r.waiting.length > 1 && (
        <div className="rounded-3xl bg-white p-4 ring-1 ring-[#1A3323]/10" data-testid="waiting-list">
          <div className="mb-2 flex items-center gap-1.5 text-sm font-bold text-[#1A3323]"><Users className="h-4 w-4" /> Antrian ({r.waiting_count})</div>
          {r.waiting.slice(1).map((w, i) => (
            <div key={w.id} className="flex items-center justify-between border-t py-2 first:border-0">
              <span className="font-mono font-bold">{i + 2}. {w.number}</span><span className="text-sm">{w.display_name}</span>
            </div>
          ))}
          {r.waiting_count > r.waiting.length && <div className="pt-2 text-xs text-muted-foreground">+{r.waiting_count - r.waiting.length} lainnya</div>}
        </div>
      )}
      <QueueHistoryDialog route={r} />
    </div>
  );
}

export function RouteTab({ r, active, offset, onClick }) {
  const climbing = r.on_wall.find((t) => t.status === "CLIMBING");
  const over = r.on_wall.some((t) => isOverdue(t, offset));
  return (
    <button onClick={onClick} data-testid={`route-tab-${r.code}`}
      className={cn("min-w-[128px] shrink-0 rounded-2xl p-3 text-left transition-colors", active ? "bg-[#1A3323] text-[#FDFCF8]" : "bg-white text-[#1A3323] ring-1 ring-[#1A3323]/10 hover:bg-[#EDE9DC]", r.status !== "OPEN" && "opacity-60")}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-extrabold uppercase">{r.name}</span>
        {over && <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" data-testid={`route-tab-overdue-${r.code}`} />}
        {r.status !== "OPEN" && <Lock className="h-3.5 w-3.5" />}
      </div>
      <div className="mt-1 text-xs opacity-75">Menunggu <span className="font-mono font-bold">{r.waiting_count}</span></div>
      <div className="text-xs opacity-75">Kini <span className="font-mono font-bold">{climbing?.number || "—"}</span></div>
    </button>
  );
}
